/**
 * VaultShareModal: Manage sharing for an existing folder.
 * Shows current shared users, allows adding/removing users.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  X,
  Search,
  Check,
  Users,
  Trash2,
  Globe,
  Lock,
} from 'lucide-react';
import type {
  VaultFolder,
  VaultFolderShare,
  VaultVisibility,
  VaultUser,
} from '../../types/vault.types';
import { VaultColorPicker } from './VaultColorPicker';
import { logError } from "../../lib/logError";

interface VaultShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  folder: VaultFolder;
  vaultUsers: VaultUser[];
  currentUserId: string;
  onUpdateVisibility: (folderId: string, visibility: VaultVisibility, sharedWith?: string[]) => Promise<void>;
  onUpdateColor: (folderId: string, color: string) => Promise<void>;
  onGetShares: (folderId: string) => Promise<VaultFolderShare[]>;
}

export function VaultShareModal({
  isOpen,
  onClose,
  folder,
  vaultUsers,
  currentUserId,
  onUpdateVisibility,
  onUpdateColor,
  onGetShares,
}: VaultShareModalProps) {
  const [visibility, setVisibility] = useState<VaultVisibility>(folder.visibility);
  const [color, setColor] = useState(folder.color);
  const [shares, setShares] = useState<VaultFolderShare[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingShares, setLoadingShares] = useState(false);

  const isOwner = folder.created_by === currentUserId;

  // Fetch existing shares on open
  const loadShares = useCallback(async () => {
    if (!isOpen || !folder.id) return;
    setLoadingShares(true);
    try {
      const shareData = await onGetShares(folder.id);
      setShares(shareData);
      setSelectedUsers(shareData.map(s => s.shared_with_user_id));
    } catch {
      console.error('[Vault] Error loading shares');
    } finally {
      setLoadingShares(false);
    }
  }, [isOpen, folder.id, onGetShares]);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  useEffect(() => {
    setVisibility(folder.visibility);
    setColor(folder.color);
  }, [folder]);

  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return vaultUsers;
    const q = userSearch.toLowerCase();
    return vaultUsers.filter(u =>
      (u.full_name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [vaultUsers, userSearch]);

  const toggleUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Update color if changed
      if (color !== folder.color) {
        await onUpdateColor(folder.id, color);
      }

      // Update visibility if changed
      if (visibility !== folder.visibility || visibility === 'shared') {
        await onUpdateVisibility(folder.id, visibility, visibility === 'shared' ? selectedUsers : undefined);
      }

      onClose();
    } catch (err) {
      logError('[Vault] Failed to save settings:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const visibilityOptions: { value: VaultVisibility; icon: typeof Globe; label: string; desc: string }[] = [
    { value: 'public', icon: Globe, label: 'Public', desc: 'All vault users' },
    { value: 'private', icon: Lock, label: 'Private', desc: 'Only you' },
    { value: 'shared', icon: Users, label: 'Shared', desc: 'Specific users' },
  ];

  return (
    <div className="vault-modal-overlay" onClick={onClose}>
      <div className="vault-share-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="vault-modal-header">
          <div className="vault-modal-title">
            <Users size={20} />
            <h3>Folder Settings</h3>
          </div>
          <button className="vault-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="vault-modal-subtitle">
          {folder.name}
        </div>

        {!isOwner ? (
          <div className="vault-modal-section">
            <div className="vault-share-info">
              This folder was shared with you. Only the owner can change settings.
            </div>
          </div>
        ) : (
          <>
            {/* Color */}
            <div className="vault-modal-section">
              <label className="vault-modal-label">Color</label>
              <VaultColorPicker selectedColor={color} onSelect={setColor} size="sm" />
            </div>

            {/* Visibility */}
            <div className="vault-modal-section">
              <label className="vault-modal-label">Visibility</label>
              <div className="vault-visibility-grid compact">
                {visibilityOptions.map(opt => {
                  const Icon = opt.icon;
                  const isActive = visibility === opt.value;
                  return (
                    <button
                      key={opt.value}
                      className={`vault-visibility-card compact ${isActive ? 'active' : ''}`}
                      onClick={() => setVisibility(opt.value)}
                      type="button"
                    >
                      <Icon size={16} />
                      <span className="vault-visibility-label">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* User picker when shared */}
            {visibility === 'shared' && (
              <div className="vault-modal-section">
                <label className="vault-modal-label">
                  Shared With {loadingShares ? '(loading...)' : `(${selectedUsers.length})`}
                </label>

                {/* Current shares */}
                {shares.length > 0 && (
                  <div className="vault-current-shares">
                    {shares.map(share => (
                      <div key={share.id} className="vault-share-row">
                        <div className="vault-user-avatar">
                          {(share.shared_with_name || share.shared_with_email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="vault-user-info">
                          <span className="vault-user-name">{share.shared_with_name || 'Unnamed'}</span>
                          {share.shared_with_email && (
                            <span className="vault-user-email">{share.shared_with_email}</span>
                          )}
                        </div>
                        <button
                          className="vault-share-remove"
                          onClick={() => toggleUser(share.shared_with_user_id)}
                          title="Remove access"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add users */}
                <div className="vault-user-search-wrap">
                  <Search size={14} className="vault-user-search-icon" />
                  <input
                    className="vault-user-search-input"
                    type="text"
                    placeholder="Search users to add..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                  />
                </div>

                <div className="vault-user-list compact">
                  {filteredUsers.map(user => {
                    const isSelected = selectedUsers.includes(user.id);
                    return (
                      <button
                        key={user.id}
                        className={`vault-user-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleUser(user.id)}
                        type="button"
                      >
                        <div className="vault-user-avatar">
                          {(user.full_name || user.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="vault-user-info">
                          <span className="vault-user-name">{user.full_name || 'Unnamed'}</span>
                        </div>
                        {isSelected && <Check size={14} className="vault-user-check" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="vault-modal-footer">
              <button className="vault-modal-btn secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                className="vault-modal-btn primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
